import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import {
  Lock, Check, ArrowRight, ArrowLeft, CalendarClock, MapPin, Coins,
  BookOpen, Loader2, ShieldCheck, Sparkles,
} from "lucide-react";
import RichText from "@/components/RichText";

const useCheckout = () => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const buy = async (product_type, slug) => {
    if (!user) {
      toast.info("Please sign in to purchase");
      login();
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/checkout/session", {
        product_type, slug, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };
  return { buy, loading };
};

const BundleDetail = () => {
  const navigate = useNavigate();
  const { hasGuide } = useAuth();
  const { buy, loading } = useCheckout();
  const { data } = useQuery({ queryKey: ["guides"], queryFn: async () => (await api.get("/guides")).data });
  const bundle = data?.bundle;
  const COUNTRIES = ["italy", "france", "spain", "germany", "sweden", "uae", "portugal", "usa", "canada"];
  const COUNTRY_NAMES = ["Italy", "France", "Spain", "Germany", "Sweden", "UAE", "Portugal", "United States", "Canada"];
  const owns = COUNTRIES.every((s) => hasGuide(s));

  if (!bundle) return <div className="min-h-screen flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>;

  return (
    <div className="bg-[#F9F9F7] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-8 text-sm"><ArrowLeft className="w-4 h-4" />Back to guides</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="relative aspect-[3/4] max-w-md overflow-hidden border border-stone-200">
            <img src={bundle.cover_url} alt="Bundle" className="w-full h-full object-cover" />
          </div>
          <div>
            <Badge className="bg-[#C2410C] text-white rounded-none border-0 mb-4">Best value · Save 33%</Badge>
            <h1 className="font-serif text-5xl font-medium text-stone-900">{bundle.title}</h1>
            <p className="text-stone-600 mt-2 text-lg">{bundle.tagline}</p>
            <p className="mt-6 text-stone-700 leading-relaxed">{bundle.description}</p>
            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-md">
              {COUNTRY_NAMES.map((c) => (
                <li key={c} className="flex items-center gap-2 text-stone-700"><Check className="w-4 h-4 text-[#166534]" />{c}</li>
              ))}
            </ul>
            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-serif text-5xl text-stone-900">${bundle.price.toFixed(2)}</span>
              <span className="text-stone-400 line-through">$44.91</span>
            </div>
            {owns ? (
              <Button asChild className="mt-6 bg-[#166534] hover:bg-[#14532d] text-white rounded-none px-8 py-6"><Link to="/library" data-testid="bundle-owned-btn">Go to My Library <ArrowRight className="w-4 h-4 ml-2" /></Link></Button>
            ) : (
              <Button data-testid="bundle-buy-btn" onClick={() => buy("bundle", "all-access-bundle")} disabled={loading} className="mt-6 bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none px-8 py-6 text-base">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting…</> : <>Buy the bundle · ${bundle.price.toFixed(2)}</>}
              </Button>
            )}
            <p className="mt-4 flex items-center gap-2 text-xs text-stone-500"><ShieldCheck className="w-4 h-4" />Secure one-time payment via Stripe. Instant access.</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const GuideDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { buy, loading } = useCheckout();
  const isBundle = slug === "all-access-bundle";

  const { data: g, isLoading } = useQuery({
    queryKey: ["guide", slug],
    queryFn: async () => (await api.get(`/guides/${slug}`)).data,
    enabled: !isBundle,
  });

  if (isBundle) return <BundleDetail />;

  if (isLoading) return <div className="min-h-screen flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>;
  if (!g) return <div className="min-h-screen flex justify-center items-center text-stone-600">Guide not found.</div>;

  return (
    <div className="bg-[#F9F9F7] min-h-screen">
      <Seo
        title={`Moving to ${g.country} — Relocation Guide (Visas, Taxes, Cost of Living) | UMoooV`}
        description={`How to move to ${g.country}: ${g.tagline} A complete relocation guide covering visas & residency, taxes, housing, healthcare, banking and cost of living, plus a first-90-days checklist. ${g.teaser}`.slice(0, 300)}
        path={`/guides/${g.slug}`}
        image={g.cover_url}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": `${g.country} Relocation Guide`,
          "image": g.cover_url,
          "description": g.teaser,
          "category": "Relocation Guides",
          "brand": { "@type": "Brand", "name": "UMoooV" },
          "offers": {
            "@type": "Offer",
            "url": `https://umooov.info/guides/${g.slug}`,
            "price": g.price?.toFixed(2),
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition",
          },
        }}
      />
      {/* Hero header */}
      <div className="relative h-[46vh] min-h-[360px] overflow-hidden">
        <img src={g.cover_url} alt={g.country} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-5xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-10">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white/80 hover:text-white mb-auto mt-8 text-sm"><ArrowLeft className="w-4 h-4" />All guides</button>
          <span className="text-5xl mb-3">{g.flag}</span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-white">{g.country}</h1>
          <p className="text-stone-100/90 mt-2 text-lg max-w-xl">{g.tagline}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-6 text-sm text-stone-600 border-b border-stone-200 pb-6 mb-8">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#9A3412]" />Capital: {g.capital}</span>
            <span className="flex items-center gap-2"><Coins className="w-4 h-4 text-[#9A3412]" />{g.local_currency}</span>
            <span className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#9A3412]" />Updated {g.last_updated}</span>
          </div>

          {/* Key facts */}
          {g.key_facts && (
            <div data-testid="key-facts-box" className="bg-stone-900 text-white p-6 md:p-8 mb-10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#F0A868] mb-5 flex items-center gap-2"><Sparkles className="w-4 h-4" />Key facts</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Language</p>
                  <p className="font-serif text-lg leading-snug">{g.key_facts.language}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Best visa route</p>
                  <p className="font-serif text-lg leading-snug">{g.key_facts.best_visa}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Residency path</p>
                  <p className="font-serif text-lg leading-snug">{g.key_facts.residency}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">The verdict</p>
                  <p className="font-serif text-lg leading-snug italic text-[#F0A868]">{g.key_facts.verdict}</p>
                </div>
              </div>
            </div>
          )}

          <p className="font-reader text-lg text-stone-700 leading-relaxed mb-10">{g.teaser}</p>

          <h2 className="font-serif text-3xl font-medium text-stone-900 mb-6">What's inside</h2>
          <div className="space-y-5">
            {g.sections.map((s, i) => (
              <div key={i} data-testid={`section-${i}`} className="border border-stone-200 bg-white">
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="font-serif text-xl text-stone-900">{i + 1}. {s.title}</h3>
                  {i === 0 ? (
                    <Badge className="bg-[#166534]/10 text-[#166534] rounded-none border-0">Free preview</Badge>
                  ) : s.locked ? (
                    <Lock className="w-4 h-4 text-stone-400" />
                  ) : (
                    <Check className="w-4 h-4 text-[#166534]" />
                  )}
                </div>
                {(i === 0 || !s.locked) && (
                  <div className="px-5 pb-5 font-reader text-stone-700 leading-relaxed">
                    <RichText text={s.content} headingClass="text-stone-900" />
                  </div>
                )}
                {s.locked && i !== 0 && (
                  <p className="px-5 pb-5 text-sm text-stone-400 italic">{s.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar buy box */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 border border-stone-200 bg-white p-7">
            {g.entitled ? (
              <>
                <Badge className="bg-[#166534] text-white rounded-none border-0 mb-4"><Check className="w-3 h-3 mr-1" />In your library</Badge>
                <p className="text-stone-600 text-sm mb-6">You own this guide. Read it in-app or download the print-ready PDF.</p>
                <Button asChild className="w-full bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none py-6"><Link to={`/read/${g.slug}`} data-testid="read-now-btn"><BookOpen className="w-4 h-4 mr-2" />Read now</Link></Button>
              </>
            ) : (
              <>
                <p className="text-sm uppercase tracking-widest text-stone-500 mb-1">One-time purchase</p>
                <div className="font-serif text-5xl text-stone-900 mb-1">${g.price.toFixed(2)}</div>
                <p className="text-stone-500 text-sm mb-6">Lifetime access · all devices</p>
                <Button data-testid="buy-guide-btn" onClick={() => buy("guide", g.slug)} disabled={loading} className="w-full bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none py-6 text-base">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting…</> : <>Buy this guide</>}
                </Button>
                <ul className="mt-6 space-y-3 text-sm text-stone-600">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#166534]" />In-app reader + dark mode</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#166534]" />Print-ready PDF download</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#166534]" />Read on web & mobile</li>
                </ul>
                <div className="mt-6 pt-6 border-t border-stone-200">
                  <p className="text-sm text-stone-600 mb-3">Planning to compare countries?</p>
                  <Link to="/guides/all-access-bundle" data-testid="upsell-bundle-link" className="text-sm font-medium text-[#9A3412] flex items-center gap-1 hover:gap-2 transition-all">Get all 9 for $29.99 <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </>
            )}
            <p className="mt-6 flex items-center gap-2 text-xs text-stone-500"><ShieldCheck className="w-4 h-4" />Secure Stripe checkout</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default GuideDetail;
